<template>
  <form @submit.prevent="submit" class="cb-form">
    <!-- Warning panel (softer than mark-failed since this is reversible) -->
    <div class="cb-warn">
      <div class="warn-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <path d="M17 3l4 4-4 4M21 7H8M7 21l-4-4 4-4M3 17h13" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>
      <div class="warn-copy">
        <div class="warn-title">Change this order's bank</div>
        <div v-if="orderRow.paymentStatus === 'approved'" class="warn-body">
          This order is <strong>approved</strong>. Switching banks will reverse
          <strong>₹{{ formatAmount(amountInPlay) }}</strong> + commission from the current bank
          and apply it to the new bank.
        </div>
        <div v-else class="warn-body">
          This will rewire the order to a different bank. No balance change happens until the order is approved.
        </div>
      </div>
    </div>

    <!-- Current bank summary -->
    <div class="cb-summary">
      <div class="sum-row">
        <span class="sum-label">Current bank</span>
        <span class="sum-value">{{ orderRow.validatorUsername || '—' }}</span>
      </div>
      <div class="sum-row">
        <span class="sum-label">Current UPI</span>
        <span class="sum-value mono break">{{ orderRow.validatorUPIID || '—' }}</span>
      </div>
      <div class="sum-row">
        <span class="sum-label">Status</span>
        <span class="sum-value upper">{{ orderRow.paymentStatus }}</span>
      </div>
      <div class="sum-row">
        <span class="sum-label">Amount</span>
        <span class="sum-value">₹{{ formatAmount(amountInPlay) }}</span>
      </div>
    </div>

    <!-- Bank picker -->
    <div class="field">
      <label>Pick the new bank <span class="req">*</span></label>
      <div class="input-wrap">
        <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="7" />
          <path d="M20 20l-3.5-3.5" stroke-linecap="round" />
        </svg>
        <input
          v-model="bankSearch"
          type="text"
          class="input has-icon"
          placeholder="Search by username, name, UPI ID, account, bank…"
          @focus="loadIfEmpty"
        />
      </div>

      <div v-if="isLoading" class="picker-hint">
        <span class="spinner-mini spinner-mini-dark"></span>
        Loading banks…
      </div>
      <div v-else-if="filteredBanks.length === 0" class="picker-hint">
        {{ bankSearch ? 'No banks match.' : 'Start typing to search…' }}
      </div>
      <div v-else class="bank-list">
        <div
          v-for="bank in filteredBanks"
          :key="bank.username"
          class="bank-row"
          :class="{ 'is-selected': selectedBank && selectedBank.username === bank.username }"
          @click="selectedBank = bank"
        >
          <div class="bank-row-top">
            <div class="bank-name">{{ bank.name || bank.username }}</div>
            <span
              v-if="bankStatusLabel(bank)"
              class="bank-status"
              :class="bankStatusClass(bank)"
            >{{ bankStatusLabel(bank) }}</span>
          </div>
          <div class="bank-meta">
            <span class="mono">{{ bank.username }}</span>
            <span v-if="bank.upiid"> · <span class="mono">{{ bank.upiid }}</span></span>
            <span v-if="bank.bankName"> · {{ bank.bankName }}</span>
            <span v-if="bank.accountNumber"> · <span class="mono">{{ bank.accountNumber }}</span></span>
          </div>
        </div>
      </div>
    </div>

    <!-- Reason -->
    <div class="field">
      <label>Reason <span class="req">*</span></label>
      <textarea
        v-model.trim="reason"
        rows="2"
        maxlength="500"
        class="textarea"
        placeholder="Why is the bank being changed? (e.g. customer paid to a previous order's QR)"
      ></textarea>
      <div class="field-foot">
        <span class="field-hint">Stored on the audit row.</span>
        <span class="char-count">{{ reason.length }}/500</span>
      </div>
    </div>

    <p v-if="errorMsg" class="form-alert">{{ errorMsg }}</p>

    <div class="form-actions">
      <button
        type="button"
        class="btn btn-secondary"
        @click="$emit('close')"
        :disabled="isSubmitting"
      >Cancel</button>
      <button
        type="submit"
        class="btn btn-primary"
        :disabled="isSubmitting || !selectedBank || !reason.trim()"
      >
        <span v-if="!isSubmitting">Change bank</span>
        <span v-else class="btn-loading">
          <span class="spinner-mini"></span>
          Working…
        </span>
      </button>
    </div>
  </form>
</template>

<script>
import http from "../../http-common";

export default {
  name: "change-bank-form",
  props: {
    orderRow: { type: Object, required: true },
  },
  emits: ["close", "changed"],
  data() {
    return {
      banks: [],
      isLoading: false,
      bankSearch: "",
      selectedBank: null,
      reason: "",
      errorMsg: null,
      isSubmitting: false,
    };
  },
  computed: {
    amountInPlay() {
      const ap = parseFloat(this.orderRow.approved_amount || 0);
      return ap > 0 ? ap : parseFloat(this.orderRow.amount || 0);
    },
    filteredBanks() {
      // Always exclude the order's current bank from the picker.
      const list = this.banks.filter(b => b.username !== this.orderRow.validatorUsername);
      const q = this.bankSearch.trim().toLowerCase();
      if (!q) return list.slice(0, 50);
      return list.filter(b => (
        (b.username || '').toLowerCase().includes(q)
        || (b.name || '').toLowerCase().includes(q)
        || (b.upiid || '').toLowerCase().includes(q)
        || (b.accountNumber || '').toLowerCase().includes(q)
        || (b.bankName || '').toLowerCase().includes(q)
      )).slice(0, 50);
    },
  },
  async created() {
    await this.loadBanks();
  },
  methods: {
    formatAmount(n) {
      const v = parseFloat(n || 0);
      return Number.isFinite(v) ? v.toFixed(2) : '0.00';
    },
    async loadIfEmpty() {
      if (this.banks.length === 0 && !this.isLoading) {
        await this.loadBanks();
      }
    },
    bankStatusLabel(bank) {
      // is_deleted takes precedence — a bank can be both inactive AND
      // deleted; we show whichever is most actionable info.
      if (Number(bank.is_deleted) === 1) return 'Deleted';
      if (Number(bank.status) === 0) return 'Inactive';
      return null;
    },
    bankStatusClass(bank) {
      if (Number(bank.is_deleted) === 1) return 'status-deleted';
      if (Number(bank.status) === 0) return 'status-inactive';
      return '';
    },
    async loadBanks() {
      this.isLoading = true;
      try {
        // Reuse /users — vendor-scoped, returns role='user' rows with
        // upiid / accountNumber / ifsc / bankName / branch info already.
        // status=any → active + inactive + soft-deleted. We deliberately
        // include retired banks so a vendor can attribute an order to a
        // bank the customer paid to before it was deactivated/deleted.
        // Dropdown badges make inactive / deleted picks deliberate.
        // Cap at 500 — a vendor with more banks than that should use the
        // search box, the dropdown caps display at 50 anyway.
        const resp = await http.get('/users?first=500&page=1&status=any');
        if (resp.status === 200) {
          this.banks = (resp.data && resp.data.data) || [];
        }
      } catch (e) {
        this.errorMsg = e.message || 'Failed to load banks.';
      } finally {
        this.isLoading = false;
      }
    },
    async submit() {
      this.errorMsg = null;
      if (!this.selectedBank) {
        this.errorMsg = "Please select a target bank.";
        return;
      }
      if (!this.reason || !this.reason.trim()) {
        this.errorMsg = "Please provide a reason.";
        return;
      }
      this.isSubmitting = true;
      try {
        await this.$store.dispatch("payin/changePayInOrderBank", {
          refID: this.orderRow.refID,
          newValidatorUsername: this.selectedBank.username,
          reason: this.reason.trim(),
        });
        this.$emit("changed", `Bank changed to ${this.selectedBank.username}.`);
      } catch (e) {
        this.errorMsg = e.message || "Failed to change bank.";
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.cb-form { display: flex; flex-direction: column; gap: 14px; }

.cb-warn {
  display: flex;
  gap: 12px;
  padding: 12px 14px;
  background: #fbfaf7;
  border: 1px solid #ede9df;
  border-radius: 12px;
  color: #475569;
}
.warn-icon {
  width: 32px; height: 32px;
  border-radius: 8px;
  background: #eef2f7;
  color: #1B2A47;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.warn-icon svg { width: 18px; height: 18px; }
.warn-copy { flex: 1; min-width: 0; font-size: 12.5px; line-height: 1.55; }
.warn-title { font-weight: 700; font-size: 13px; margin-bottom: 4px; color: #1b2a47; }

.cb-summary {
  background: #fbfaf7;
  border: 1px solid #ede9df;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.sum-row {
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 8px;
  align-items: baseline;
  font-size: 12.5px;
}
.sum-label { color: #94a3b8; font-weight: 500; }
.sum-value { color: #1b2a47; font-weight: 600; }
.sum-value.mono { font-family: "SFMono-Regular", Menlo, monospace; font-size: 11.5px; color: #334155; }
.sum-value.break { word-break: break-all; overflow-wrap: anywhere; }
.upper { text-transform: uppercase; letter-spacing: 0.03em; }

.field { display: flex; flex-direction: column; }
.field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
}
.req { color: #dc2626; }

.input-wrap { position: relative; display: flex; align-items: center; }
.input-icon {
  position: absolute;
  left: 12px;
  width: 16px; height: 16px;
  color: #94a3b8;
  pointer-events: none;
}
.input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #fbfaf7;
  color: #0f172a;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.input.has-icon { padding-left: 38px; }
.input:focus {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(27, 42, 71, 0.08);
}

.picker-hint {
  margin-top: 8px;
  font-size: 12px;
  color: #94a3b8;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

/* Bank list */
.bank-list {
  margin-top: 8px;
  border: 1px solid #ede9df;
  border-radius: 10px;
  max-height: 240px;
  overflow-y: auto;
  background: #fff;
}
.bank-row {
  padding: 10px 12px;
  border-bottom: 1px solid #f4f0e5;
  cursor: pointer;
  transition: background 0.12s;
}
.bank-row:last-child { border-bottom: none; }
.bank-row:hover { background: #fbfaf7; }
.bank-row.is-selected { background: #eef2f7; border-left: 3px solid #1B2A47; padding-left: 9px; }

.bank-row-top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 2px;
}
.bank-name {
  font-size: 13px;
  font-weight: 600;
  color: #1b2a47;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.bank-status {
  display: inline-flex;
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  flex-shrink: 0;
}
.status-deleted  { background: #fee2e2; color: #991b1b; }
.status-inactive { background: #fef3c7; color: #92400e; }

.bank-meta {
  font-size: 11.5px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mono { font-family: "SFMono-Regular", Menlo, monospace; }

.textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #fbfaf7;
  color: #0f172a;
  font-family: inherit;
  outline: none;
  resize: vertical;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.textarea:focus {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(27, 42, 71, 0.08);
}
.field-foot {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: 6px;
  font-size: 11px;
}
.field-hint { color: #94a3b8; }
.char-count { color: #94a3b8; font-variant-numeric: tabular-nums; }

.form-alert {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12.5px;
  margin: 0;
  font-weight: 500;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 4px;
}
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  outline: none;
  font-family: inherit;
}
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn-secondary {
  background: #fff;
  color: #475569;
  border-color: #e2e8f0;
}
.btn-secondary:hover:not(:disabled) { background: #fbfaf7; border-color: #cbd5e1; color: #1b2a47; }
.btn-primary {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 6px 14px -6px rgba(27, 42, 71, 0.45);
}
.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px -8px rgba(27, 42, 71, 0.55);
}
.btn-loading { display: inline-flex; align-items: center; gap: 8px; }

.spinner-mini {
  width: 12px; height: 12px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  animation: spin 0.7s linear infinite;
  display: inline-block;
}
.spinner-mini-dark {
  border-color: #cbd5e1;
  border-top-color: #1B2A47;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
